import { toast } from 'react-toastify';
import { QueryClient } from '@tanstack/react-query';
import { useKeycloak } from 'src/stores/keycloak';
import { useMagicLink } from 'src/stores/magicLink';
import { getAuthProvider, isAuthDisabled } from 'src/auth/authConfig';
import { getMsalInstance } from 'src/auth/msalInstanceManager';
import {
  captureSessionException,
  captureSessionMessage,
} from 'src/utils/sentry/sessionSentry';

/**
 * Guards against concurrent invocations of handleSessionTermination.
 * Both http.ts (HTTP layer) and main.tsx (React Query layer) can detect the
 * same "Token has been invalidated" error and call this function independently.
 * The flag ensures only the first call runs; subsequent calls are no-ops.
 * It resets on the next page navigation (module is re-evaluated after reload).
 */
let isTerminating = false;

/**
 * Handles session termination when token has been invalidated
 *
 * This function:
 * 1. Shows a toast message: "your session has been terminated"
 * 2. Clears React Query cache
 * 3. Logs out the user from the current auth provider
 * 4. Redirects to the root path (which will trigger login via AuthProtectedRoute)
 *
 * @param queryClient - The React Query client instance to clear cache from
 */
export async function handleSessionTermination(
  queryClient: QueryClient,
): Promise<void> {
  // Prevent duplicate execution when both http.ts and React Query fire this
  if (isTerminating) {
    return;
  }
  isTerminating = true;

  captureSessionMessage('UI session termination started', 'warning', {
    event: 'session.termination.tokenInvalidated',
  });

  // Show toast message
  toast.error('your session has been terminated', {
    autoClose: 5000,
  });

  // Clear React Query cache
  queryClient.clear();

  // Logout from the current auth provider
  if (!isAuthDisabled()) {
    const authProvider = getAuthProvider();

    try {
      if (authProvider === 'keycloak') {
        const keycloakState = useKeycloak.getState();
        if (keycloakState.actions && keycloakState.keycloak) {
          await keycloakState.actions.logout();
        }
      } else if (authProvider === 'magic-link') {
        const magicLinkState = useMagicLink.getState();
        if (magicLinkState.actions) {
          // Clear tokens for magic link
          magicLinkState.actions.clearTokens();
        }
      } else if (authProvider === 'entra') {
        // For Entra ID, we need to logout via MSAL
        try {
          const instance = getMsalInstance();
          if (instance) {
            const accounts = instance.getAllAccounts();
            if (accounts.length > 0) {
              await instance.logoutPopup({
                account: accounts[0],
              });
            }
          }
        } catch (error) {
          // If popup logout fails, try redirect logout
          console.warn('Popup logout failed, trying redirect:', error);
          try {
            const instance = getMsalInstance();
            if (instance) {
              await instance.logoutRedirect();
            }
          } catch (redirectError) {
            console.error('Logout redirect also failed:', redirectError);
          }
        }
      }
    } catch (error) {
      // Log error but continue with redirect
      console.error('Error during logout:', error);
      captureSessionException(error, {
        event: 'session.termination.logoutFailed',
        authProvider: authProvider ?? undefined,
      });
    }
  }

  // Force hard redirect to root path
  // This will trigger AuthProtectedRoute which will show the login page
  globalThis.location.href = '/';

  captureSessionMessage('UI session termination redirected to login', 'info', {
    event: 'session.termination.redirected',
  });
}
